import * as Sentry from '@sentry/react'
import {QueryClient, QueryClientProvider} from '@tanstack/react-query'
import {ReactQueryDevtools} from '@tanstack/react-query-devtools'
import {setupAxios} from 'app/modules/auth'
import {AppRoutes} from 'app/routing/AppRoutes'
import axios from 'axios'
import {Chart, registerables} from 'chart.js'
import ChunkErrorBoundary from 'common/components/ChunkErrorBoundary'
import {releaseChunkReloadBudgetWhenStable, reloadForChunkError} from 'common/helpers/chunkLoad'
import {NuqsAdapter} from 'nuqs/adapters/react-router/v7'
import {PostHogProvider} from 'posthog-js/react'
import {FC} from 'react'
import {createRoot} from 'react-dom/client'
import {Provider} from 'react-redux'
import {Fragment} from 'react/jsx-runtime'
import {PersistGate} from 'redux-persist/integration/react'
import store, {persistor} from 'setup/redux/Store'

import '_metronic/assets/fonticon/fonticon.css'
import '_metronic/assets/keenicons/duotone/style.css'
import '_metronic/assets/keenicons/outline/style.css'
import '_metronic/assets/keenicons/solid/style.css'
import '_metronic/assets/sass/style.react.scss'
import {WithChildren} from '_metronic/helpers'
import {MetronicI18nProvider, isRtl} from '_metronic/i18n/Metronici18n'
import {I18nProvider} from '_metronic/i18n/i18nProvider'

import './sentry'
import {initOtel} from './telemetry/otel'

initOtel()

// Deliberately no `event.preventDefault()` here — cancelling the event stops vite
// from rethrowing, which makes the import resolve with `undefined` and crashes
// React.lazy. Let the error through so ChunkErrorBoundary can handle it.
window.addEventListener('vite:preloadError', () => {
  reloadForChunkError('vite:preloadError')
})

// Reset state once the page has stayed up long enough to count as a good load
window.addEventListener('load', releaseChunkReloadBudgetWhenStable)

if (isRtl()) {
  await import('_metronic/assets/css/style.rtl.css')
} else {
  await import('_metronic/assets/sass/style.scss')
}

/**
 * Creates `axios-mock-adapter` instance for provided `axios` instance, add
 * basic Metronic mocks and returns it.
 *
 * @see https://github.com/ctimmerm/axios-mock-adapter
 */
/**
 * Inject Metronic interceptors for axios.
 *
 * @see https://github.com/axios/axios#interceptors
 */
setupAxios(axios)
Chart.register(...registerables)

const postHogApiKey = import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY || ''

const HogProvider: FC<WithChildren> = ({children}) => {
  if (postHogApiKey) {
    return (
      <PostHogProvider
        apiKey={postHogApiKey}
        options={{
          api_host: import.meta.env.VITE_APP_PUBLIC_POSTHOG_HOST || 'https://eu.i.posthog.com',
        }}
      >
        {children}
      </PostHogProvider>
    )
  }
  return <Fragment>{children}</Fragment>
}

const queryClient = new QueryClient()
const container = document.getElementById('root')

if (container) {
  createRoot(container, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn('Uncaught error', error, errorInfo.componentStack)
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(
    <HogProvider>
      <QueryClientProvider client={queryClient}>
        <MetronicI18nProvider>
          <Provider store={store}>
            <PersistGate persistor={persistor}>
              <ChunkErrorBoundary>
                <NuqsAdapter>
                  <I18nProvider>
                    <AppRoutes />
                  </I18nProvider>
                </NuqsAdapter>
              </ChunkErrorBoundary>
            </PersistGate>
          </Provider>
        </MetronicI18nProvider>
        <ReactQueryDevtools initialIsOpen={false} position='left' />
      </QueryClientProvider>
    </HogProvider>
  )
}
